import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import {
  type BillingPeriod,
  PRICING,
  type TierId,
  clampStreams,
  formatPriceCents,
  getTier,
  isTierId,
  monthlyTotalCents,
  priceForPeriodCents,
} from '@gobingebox/shared';
import { PageHero } from '@/components/PagePlaceholder';
import { Alert } from '@/components/ui/Alert';
import { Button, buttonVariants } from '@/components/ui/Button';
import { CheckMark } from '@/components/icons';
import { useAuth } from '@/features/auth/AuthContext';
import { billingApi } from '@/features/billing/api';

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2 text-sm">
      <span className="text-ink-muted">{label}</span>
      <span className="font-medium text-ink">{value}</span>
    </div>
  );
}

export function CheckoutPage() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [paying, setPaying] = useState(false);

  const tierParam = params.get('tier') ?? '';
  const period: BillingPeriod = params.get('period') === 'annual' ? 'annual' : 'monthly';
  const streams = clampStreams(Number(params.get('streams') ?? PRICING.includedStreams));

  const config = useQuery({ queryKey: ['billing-config'], queryFn: billingApi.config });
  const stripePromise = useMemo(
    () => (config.data?.publishableKey ? loadStripe(config.data.publishableKey) : null),
    [config.data?.publishableKey],
  );
  const fetchClientSecret = useCallback(
    () => billingApi.checkout(tierParam as TierId, period, streams).then((r) => r.clientSecret),
    [tierParam, period, streams],
  );

  if (!isTierId(tierParam)) {
    return (
      <div className="container-page py-16">
        <Alert tone="error">
          No plan selected.{' '}
          <Link to="/plans" className="font-semibold underline">Choose a plan</Link>.
        </Alert>
      </div>
    );
  }

  const tier = getTier(tierParam);
  const total = priceForPeriodCents(tierParam, streams, period);
  const features = [
    'Full episodes — Japanese & English audio',
    tier.features.adFree ? 'Ad-free playback' : 'Ad-supported playback',
    `${Math.round(tier.features.merchDiscountPct * 100)}% merch discount`,
    `${streams} simultaneous stream${streams > 1 ? 's' : ''}`,
    ...(tier.features.offlineDownloads ? ['Offline downloads'] : []),
    ...(tier.features.gamesAccess ? ['Games access'] : []),
    ...(tier.features.highestQuality ? ['4K / highest quality'] : []),
    ...(tier.features.earlyAccess ? ['Early episode drops'] : []),
  ];

  const next = `/checkout?tier=${tierParam}&period=${period}&streams=${streams}`;

  return (
    <div>
      <PageHero eyebrow="Checkout" title={`${tier.name} plan`} />
      <div className="container-page grid gap-5 pb-16 lg:grid-cols-[1.1fr_1fr]">
        <section className="card p-7">
          <h2 className="font-display text-lg font-bold text-ink">Your plan</h2>
          <div className="mt-3 divide-y divide-border">
            <SummaryRow label="Plan" value={tier.name} />
            <SummaryRow label="Billing" value={period === 'annual' ? 'Annual (one-time)' : 'Monthly'} />
            <SummaryRow label="Simultaneous streams" value={String(streams)} />
            <SummaryRow
              label={period === 'annual' ? 'Total (12 months)' : 'Monthly total'}
              value={
                period === 'annual'
                  ? formatPriceCents(total)
                  : `${formatPriceCents(monthlyTotalCents(tierParam, streams))}/mo`
              }
            />
          </div>
          <ul className="mt-5 space-y-2.5">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-2.5 text-sm text-ink">
                <CheckMark size={18} />
                {f}
              </li>
            ))}
          </ul>
          <Link to="/plans" className="mt-6 inline-block text-sm font-medium text-ink-muted hover:text-ink">
            ← Change plan
          </Link>
        </section>

        <section className="card flex flex-col p-7">
          <h2 className="font-display text-lg font-bold text-ink">Payment</h2>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-display text-3xl font-extrabold text-ink">{formatPriceCents(total)}</span>
            <span className="text-sm text-ink-muted">
              {period === 'annual' ? 'billed once · 12 months' : 'per month · cancel anytime'}
            </span>
          </div>

          {!user ? (
            <div className="mt-6 space-y-3">
              <Alert tone="info">Create an account or sign in to continue to payment.</Alert>
              <div className="flex gap-2">
                <Link to={`/signup?next=${encodeURIComponent(next)}`} className={buttonVariants({ variant: 'primary', size: 'lg', className: 'flex-1' })}>
                  Create account
                </Link>
                <Link to={`/login?next=${encodeURIComponent(next)}`} className={buttonVariants({ variant: 'secondary', size: 'lg', className: 'flex-1' })}>
                  Sign in
                </Link>
              </div>
            </div>
          ) : config.isLoading ? (
            <p className="mt-6 text-sm text-ink-faint">Loading payment…</p>
          ) : !config.data?.enabled || !stripePromise ? (
            <Alert tone="info" className="mt-6">Payments aren't switched on yet. Check back shortly.</Alert>
          ) : !paying ? (
            <div className="mt-6">
              <Button size="lg" className="w-full" onClick={() => setPaying(true)}>
                Continue to payment
              </Button>
              <p className="mt-3 text-center text-xs text-ink-faint">
                Secure payment by Stripe. We never see your card details.
              </p>
            </div>
          ) : (
            <div className="mt-6">
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
